import React, { PropsWithChildren } from "react";
//#region types
import { FileUploaderContainerProps, FileUploaderProps, ReadingFile } from "models/propTypes/fileUploaderPropTypes";
import { UploadedFile } from "models/utilTypes/fileUtilsTypes";
import { FileConstants } from "utils/constants";
//#endregion
//#region components
import FileUploader from "./fileUploader";
import FileUploaderLite from "./fileUploaderLite";
//#endregion
//#region helpers
import { FileHelper, UtilsHelper } from "utils/helpers";
//#endregion
import cssExports from "theme/styles/main.module.scss";
import { Map } from "immutable";

const FileUploaderContainer: React.FC<FileUploaderContainerProps> = (props) => {
  const overrideEventDefaults = React.useCallback((event: Event | React.DragEvent<HTMLDivElement>) => {
    event.preventDefault();
    event.stopPropagation();
  }, []);

  React.useEffect(() => {
    window.addEventListener("dragover", overrideEventDefaults);
    window.addEventListener("drop", overrideEventDefaults);

    return () => {
      window.removeEventListener("dragover", overrideEventDefaults);
      window.removeEventListener("drop", overrideEventDefaults);
    };
  }, [overrideEventDefaults]);

  const clearErrorMessage = (action: React.Dispatch<React.SetStateAction<boolean>>) => {
    setTimeout(() => {
      action(false);
    }, 3000);
  };

  const setErrorMessage = (action: React.Dispatch<React.SetStateAction<boolean>>) => {
    action(true);
    clearErrorMessage(action);
  };

  const FileUploaderComponent: React.FC<PropsWithChildren<FileUploaderProps>> =
    props.fileUploaderType === undefined || props.fileUploaderType === "full" ? FileUploader : FileUploaderLite;

  const fileUploaderInput = React.useRef<HTMLInputElement>(null);

  const resetFileInput = () => {
    if (fileUploaderInput.current) {
      fileUploaderInput.current.value = "";
    }
  };

  const maxFileSize = React.useMemo(() => FileHelper.bytesToSize(props.maxFileSize), [props.maxFileSize]);
  const allowedMaxFileCount = props.maxFileCount ?? FileConstants.MaxNumberOfSelectedFiles;
  const existingFileCount = props.filesToUpload?.length ?? 0;

  const [dragging, setDragging] = React.useState(false);
  const [invalidFileType, setInvalidFileType] = React.useState(false);
  const [invalidFileSize, setInvalidFileSize] = React.useState(false);
  const [hasEmptyFile, setHasEmptyFile] = React.useState(false);
  const [beyondMaxAmountOfFiles, setBeyondMaxAmountOfFiles] = React.useState(false);

  const { allowedFileTypes, allowedTypesMessage, acceptedFileTypes } = React.useMemo(() => {
    const allowedFileTypes = FileHelper.getAcceptedFileTypes(
      props.allowAnyContentType,
      props.allowAnyImageType,
      props.allowedTypes
    );

    return {
      allowedFileTypes,
      allowedTypesMessage: FileHelper.generateAllowedTypesMessage(allowedFileTypes),
      acceptedFileTypes: FileHelper.generateAcceptedString(allowedFileTypes),
    };
  }, [props.allowAnyContentType, props.allowAnyImageType, props.allowedTypes]);

  // Validate filesToUpload and update state/notify parent when invalid files are detected
  // This must be in useEffect, not useMemo, to avoid setState during render
  React.useEffect(() => {
    if (props.filesToUpload && props.filesToUpload.length > 0) {
      const allowedFiles = props.filesToUpload.filter((file) =>
        FileHelper.allowUpload(file.fileName, file.contentType, allowedFileTypes)
      );

      if (allowedFiles.length !== props.filesToUpload.length) {
        setErrorMessage(setInvalidFileType);
        props.onFilesUpdated(allowedFiles);
      }
    }
  }, [props.filesToUpload, allowedFileTypes, props.onFilesUpdated]);

  const validateFilesToBeUploaded = (fileList: FileList) => {
    const filesToUpload: Array<File> = [];
    Array.prototype.forEach.call(fileList, function (file: File) {
      if (file.size > props.maxFileSize) {
        setErrorMessage(setInvalidFileSize);
        return;
      }

      if (file.size === 0) {
        setErrorMessage(setHasEmptyFile);
        return;
      }

      const allowUpload = FileHelper.allowUpload(file.name, file.type, allowedFileTypes);

      if (!allowUpload) {
        setErrorMessage(setInvalidFileType);
        return;
      }

      filesToUpload.push(file);
    });
    return filesToUpload;
  };

  const [filesToReadMap, setFilesToReadMap] = React.useState(Map<number, ReadingFile>());
  const [filesToReadFileMap, setFilesToReadFileMap] = React.useState(Map<number, File>());
  const [filesToRead, setFilesToRead] = React.useState<number[]>([]);
  const [uploadedFiles, setUploadedFiles] = React.useState<UploadedFile[]>([]);

  // Set for the lifetime of one batch -- from the moment reading starts until every file in it has
  // either finished or been removed (aborted, or errored). filesToRead reaching 0 is otherwise also
  // true whenever nothing has ever been read, so the completion effect below needs this to tell
  // "just finished" apart from "always idle".
  const hasPendingReadsRef = React.useRef(false);

  const uploadFiles = (fileList: FileList | null) => {
    if (!fileList || fileList.length <= 0) {
      return;
    }

    const totalFileCount = existingFileCount + fileList.length;

    if (totalFileCount > allowedMaxFileCount) {
      setErrorMessage(setBeyondMaxAmountOfFiles);
    }

    const files = validateFilesToBeUploaded(fileList);
    if (files.length > 0) {
      // Fires before the (async) FileReader work below, not after: onFilesUpdated only fires once
      // every file in the batch has finished being read, which is too late for anything that needs
      // to know a selection is in progress as soon as it's accepted.
      props.onReadingStarted?.();
      hasPendingReadsRef.current = true;
    }

    const filesBeingRead = files.map(readFile);
    setFilesToReadMap((state) => state.merge(filesBeingRead.map((f) => [f.id, f])));
    setFilesToReadFileMap((state) => state.merge(filesBeingRead.map((f) => [f.id, f.file])));
    setFilesToRead((state) => state.concat(filesBeingRead.map((f) => f.id)));

    resetFileInput();
  };

  const removeReadFile = (fileId: number) => {
    setFilesToReadMap((state) => state.remove(fileId));
    setFilesToReadFileMap((state) => state.remove(fileId));
    setFilesToRead((state) => state.filter((v) => v !== fileId));
  };

  const updateReadFileProgress = (fileId: number, progress: number) => {
    setFilesToReadMap((state) =>
      state.update(fileId, { id: fileId, name: "", sizeInBytes: 0, progress: 0 }, (v) => ({ ...v, progress }))
    );
  };

  const fileReaders = React.useRef<Record<number, FileReader>>({});
  const maxConcurrentReads = 3; // Process up to 3 files concurrently

  const processFile = React.useCallback(
    (fileId: number) => {
      const file = filesToReadFileMap.get(fileId);
      if (!file || !fileReaders.current) return;

      const fileReader = new FileReader();
      fileReaders.current[fileId] = fileReader;

      fileReader.onabort = function (e) {
        removeReadFile(fileId);
        delete fileReaders.current[fileId];
      };

      fileReader.onerror = function (e) {
        removeReadFile(fileId);
        delete fileReaders.current[fileId];
      };

      fileReader.onprogress = function (evt: ProgressEvent<FileReader>) {
        if (evt.lengthComputable) {
          const percentLoaded = Math.round((evt.loaded / evt.total) * 100);
          updateReadFileProgress(fileId, percentLoaded);
        }
      };

      fileReader.onloadstart = function (e) {
        updateReadFileProgress(fileId, 1);
      };

      fileReader.onload = function () {
        const result = this.result as ArrayBuffer;

        // Use requestIdleCallback to prevent UI blocking
        const processResult = () => {
          const resultAsString = FileHelper.readFileAsBase64String(result);
          const contentType = FileHelper.normalizeContentType(file.name, file.type);

          const uploadedFile: UploadedFile = {
            contentType: contentType,
            fileName: file.name,
            fileSize: file.size ?? resultAsString.length,
            data: resultAsString,
          };

          setUploadedFiles((prev) => [...prev, uploadedFile]);
          removeReadFile(fileId);
          delete fileReaders.current[fileId];
        };

        // Use requestIdleCallback if available, otherwise use setTimeout
        if (window.requestIdleCallback) {
          window.requestIdleCallback(processResult);
        } else {
          setTimeout(processResult, 0);
        }
      };

      fileReader.readAsArrayBuffer(file);
    },
    [filesToReadFileMap, removeReadFile, updateReadFileProgress]
  );

  React.useEffect(() => {
    if (filesToRead.length === 0) return;

    // Process files concurrently up to maxConcurrentReads
    const filesToProcess = filesToRead.slice(0, maxConcurrentReads);

    filesToProcess.forEach((fileId) => {
      // Only process if not already being processed
      if (!fileReaders.current[fileId]) {
        processFile(fileId);
      }
    });
  }, [filesToRead.length, processFile]);

  // Call onFilesUpdated with all uploaded files when all files are processed. Unchanged from before
  // onReadingStarted/onReadingEnded existed: still only fires when the batch produced at least one
  // UploadedFile, so a consumer that treats any call as "the user did something" (e.g. marking a
  // form field touched) sees no new calls it didn't see before. The zero-survivors case (every file
  // in the batch was stopped or errored out) goes to onReadingEnded instead, below, since there is
  // nothing here for onFilesUpdated to report.
  React.useEffect(() => {
    if (filesToRead.length !== 0) return;

    if (uploadedFiles.length > 0) {
      hasPendingReadsRef.current = false;

      // All files have been processed, append them to existing files (excluding duplicates)
      const existingFiles = props.filesToUpload ?? [];

      // Filter out duplicate files based on fileName, fileSize, and contentType
      const uniqueNewFiles = uploadedFiles.filter((newFile) => {
        return !existingFiles.some(
          (existingFile) =>
            existingFile.fileName === newFile.fileName &&
            existingFile.fileSize === newFile.fileSize &&
            existingFile.contentType === newFile.contentType
        );
      });

      const allFiles = [...existingFiles, ...uniqueNewFiles];

      // Clear the uploaded files state
      setUploadedFiles([]);

      // Use onFilesUpdated to handle multiple files at once
      props.onFilesUpdated(allFiles);
      return;
    }

    // hasPendingReadsRef, not just filesToRead reaching 0, tells a batch that really ran and ended
    // with nothing apart from one that was always idle.
    if (hasPendingReadsRef.current) {
      hasPendingReadsRef.current = false;
      props.onReadingEnded?.();
    }
  }, [filesToRead.length, uploadedFiles.length]);

  const readFile = (file: File) => {
    const fileId = UtilsHelper.getUniqueId();

    return {
      id: fileId,
      name: file.name,
      sizeInBytes: file.size ?? 0,
      progress: 0,
      file,
    };
  };

  const validateFileTypes = (file: DataTransferItem) => {
    const fileObj = file.getAsFile();
    const allowUpload = FileHelper.allowUpload(fileObj?.name, file.type, allowedFileTypes);

    if (!allowUpload) {
      setInvalidFileType(true);
      return false;
    } else {
      return true;
    }
  };

  const [allowedItemsToUpload, setAllowedItemsToUpload] = React.useState(0);

  const onUploadFiles = (fileList: FileList | null, allowedToUpload: number) => {
    if (allowedToUpload > 0) {
      uploadFiles(fileList);
    } else {
      setAllowedItemsToUpload(0);
      clearErrorMessage(setBeyondMaxAmountOfFiles);
      clearErrorMessage(setInvalidFileType);
    }
  };

  const validate = (items: DataTransferItemList, onValidateComplete?: () => void) => {
    if (items.length + existingFileCount > allowedMaxFileCount) {
      setBeyondMaxAmountOfFiles(true);
    }

    const validationResult = Array.prototype.map.call(items, (file: DataTransferItem) =>
      validateFileTypes(file)
    ) as boolean[];

    let allowedFileCount = validationResult.reduce((prev: number, current: boolean) => prev + Number(current), 0);

    if (allowedFileCount + existingFileCount > allowedMaxFileCount) {
      allowedFileCount = allowedMaxFileCount - existingFileCount;
    }

    setAllowedItemsToUpload(allowedFileCount);
    return allowedFileCount;
  };

  const dragEventCounter = React.useRef(0);

  const dragEnterListener = React.useCallback((event: React.DragEvent<HTMLDivElement>) => {
    overrideEventDefaults(event);
    dragEventCounter.current++;
    const items = event.dataTransfer.items;

    if (dragEventCounter.current > 1) {
      return;
    }

    if (items?.length > 0) {
      setDragging(true);
      validate(items);
    } else if (
      event.dataTransfer.types &&
      event.dataTransfer.types.length > 0 &&
      event.dataTransfer.types[0] === FileConstants.DragEventDataTransferType
    ) {
      // This block handles support for IE
      setDragging(true);
    }
  }, []);

  const dragLeaveListener = React.useCallback((event: React.DragEvent<HTMLDivElement>) => {
    overrideEventDefaults(event);
    dragEventCounter.current--;

    if (dragEventCounter.current === 0) {
      setDragging(false);
      setAllowedItemsToUpload(0);
      setInvalidFileType(false);
      setBeyondMaxAmountOfFiles(false);
    }
  }, []);

  const dropListener = React.useCallback((event: React.DragEvent<HTMLDivElement>) => {
    overrideEventDefaults(event);
    dragEventCounter.current = 0;
    setDragging(false);

    const hasError = invalidFileSize || invalidFileType || beyondMaxAmountOfFiles || hasEmptyFile;
    const isPossibleDragEventNotSupported =
      allowedItemsToUpload === 0 && event.dataTransfer.items.length > 0 && !hasError;

    // This block handles support for Safari
    if (isPossibleDragEventNotSupported) {
      const allowedToUpload = validate(event.dataTransfer.items);
      onUploadFiles(event.dataTransfer.files, allowedToUpload);
    } else {
      onUploadFiles(event.dataTransfer.files, allowedItemsToUpload);
    }
  }, []);

  const onSelectFileClick = React.useCallback(() => {
    fileUploaderInput.current && fileUploaderInput.current.click();
  }, []);

  const onFileChanged = React.useCallback((event: React.ChangeEvent<HTMLInputElement>) => {
    if (
      event.target.files?.length !== undefined &&
      event.target.files?.length + existingFileCount > allowedMaxFileCount
    ) {
      setBeyondMaxAmountOfFiles(true);
    }
    uploadFiles(event.target.files);
  }, []);

  const onCancelUpload = React.useCallback(
    (index: number) => {
      if (index > -1) {
        props.onCancelUpload(index);
      }
    },
    [props.onCancelUpload]
  );

  const onStopReading = React.useCallback(
    (id: number) => {
      const fileReader = fileReaders.current[id];
      if (fileReader) {
        fileReader.abort();
        delete fileReaders.current[id];
      }

      const fileInfo = filesToReadMap.get(id);
      if (fileInfo == null) {
        return;
      }
      removeReadFile(id);
    },
    [filesToReadMap]
  );

  // Cleanup file readers on unmount
  React.useEffect(() => {
    return () => {
      Object.values(fileReaders.current).forEach((reader) => {
        reader.abort();
      });
      fileReaders.current = {};
    };
  }, []);

  return (
    <FileUploaderComponent
      dragging={dragging}
      remainingFiles={filesToRead.map((id) => filesToReadMap.get(id)!)}
      disabled={props.disabled}
      readonly={props.readonly}
      invalidFileType={invalidFileType}
      invalidFileSize={invalidFileSize}
      hasEmptyFile={hasEmptyFile}
      beyondMaxAmountOfFiles={beyondMaxAmountOfFiles}
      maxFileSize={maxFileSize}
      allowedMaxFileCount={allowedMaxFileCount}
      files={props.disabled ? [] : (props.filesToUpload ?? [])}
      fileTypes={allowedTypesMessage}
      hideFileTypes={props.hideFileTypes}
      onSelectFileClick={onSelectFileClick}
      onDrag={overrideEventDefaults}
      onDragStart={overrideEventDefaults}
      onDragEnd={overrideEventDefaults}
      onDragOver={overrideEventDefaults}
      onDragEnter={props.disabled ? overrideEventDefaults : dragEnterListener}
      onDragLeave={props.disabled ? overrideEventDefaults : dragLeaveListener}
      onDrop={props.disabled ? overrideEventDefaults : dropListener}
      handleClose={onCancelUpload}
      onStopReading={onStopReading}
      onTileClick={props.openPreview}
    >
      <input
        disabled={props.disabled}
        ref={fileUploaderInput}
        type="file"
        className={cssExports.fileInput}
        onChange={onFileChanged}
        accept={acceptedFileTypes}
        multiple={!props.singleSelection}
      />
    </FileUploaderComponent>
  );
};

export default FileUploaderContainer;
